import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { EvacuationPlanModal, EvacuationPlanData } from '../../components/EvacuationPlanModal';
import { workplaceApi } from '../../services/api';
import './EvacuationPlanPage.css';

interface Workplace {
  id: number;
  name: string;
  riskLevel?: string;
  hazardClass?: string;
  address?: string;
  city?: string;
  district?: string;
  employeeCount?: number;
  sskRegistrationNo?: string;
  sgkNo?: string;
}

const RISK_LEVEL_LABEL: Record<string, string> = {
  low: 'Az Tehlikeli',
  dangerous: 'Tehlikeli',
  veryDangerous: 'Çok Tehlikeli',
  very_dangerous: 'Çok Tehlikeli'
};

const EvacuationPlanPage: React.FC = () => {
  const navigate = useNavigate();

  const [workplaces, setWorkplaces] = useState<Workplace[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [selectedWorkplace, setSelectedWorkplace] = useState<Workplace | null>(null);

  // Search & Filter
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [dropdownOpen, setDropdownOpen] = useState<boolean>(false);
  const searchWrapRef = useRef<HTMLDivElement | null>(null);

  // Evacuation Plan Settings
  const [assemblyPoint, setAssemblyPoint] = useState<string>('Ana Giriş Bahçe / Açık Alan');
  const [evacuationPlanData, setEvacuationPlanData] = useState<EvacuationPlanData | null>(null);
  const [modalOpen, setModalOpen] = useState<boolean>(false);

  const getToken = () =>
    localStorage.getItem('fieldToken') ||
    localStorage.getItem('authToken') ||
    localStorage.getItem('token');

  const getHeaders = () => {
    const token = getToken();
    const headers: Record<string, string> = {
      'Content-Type': 'application/json'
    };
    if (token) headers['Authorization'] = `Bearer ${token}`;
    const orgId = localStorage.getItem('organizationId');
    if (orgId) headers['x-organization-id'] = orgId;
    return headers;
  };

  useEffect(() => {
    fetchWorkplaces();
  }, []);

  // Click outside to close dropdown
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (searchWrapRef.current && !searchWrapRef.current.contains(event.target as Node)) {
        setDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const fetchWorkplaces = async () => {
    try {
      setLoading(true);
      const res = await workplaceApi.getAll();
      const list = (res && res.data) ? res.data : (Array.isArray(res) ? res : []);

      const normalized: Workplace[] = list.map((wp: any) => ({
        id: wp.id,
        name: wp.title || wp.name || `İşyeri #${wp.id}`,
        riskLevel: wp.dangerClass || wp.riskLevel || 'dangerous',
        hazardClass: wp.dangerClass || wp.riskLevel || 'dangerous',
        address: wp.address || [wp.district, wp.city].filter(Boolean).join(' / ') || '',
        city: wp.city,
        district: wp.district,
        employeeCount: wp.employeeCount || 1,
        sskRegistrationNo: wp.sgkNo || wp.sskRegistrationNo || '',
        sgkNo: wp.sgkNo || wp.sskRegistrationNo || ''
      }));

      setWorkplaces(normalized);

      // Check if there was a selected workplace from previous navigation or state
      const savedWpId = sessionStorage.getItem('eap_selected_workplace_id');
      if (savedWpId) {
        const found = normalized.find(w => String(w.id) === savedWpId);
        if (found) setSelectedWorkplace(found);
      }
    } catch (err) {
      console.error('Workplaces fetch error:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleSelectWorkplace = (wp: Workplace) => {
    setSelectedWorkplace(wp);
    setSearchTerm('');
    setDropdownOpen(false);
    setEvacuationPlanData(null);
    sessionStorage.setItem('eap_selected_workplace_id', String(wp.id));
  };

  const filteredWorkplaces = workplaces.filter(w =>
    w.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    (w.city && w.city.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  const handleDownloadPng = () => {
    if (!evacuationPlanData?.imageDataUrl || !selectedWorkplace) return;
    const a = document.createElement('a');
    a.href = evacuationPlanData.imageDataUrl;
    a.download = `Tahliye_Krokisi_${selectedWorkplace.name.replace(/\s+/g, '_')}.png`;
    a.click();
  };

  return (
    <div className="epp-container">
      {/* ── TOP HEADER / HERO ── */}
      <div className="epp-hero">
        <div className="epp-hero-left">
          <button
            type="button"
            className="epp-back-btn"
            onClick={() => navigate('/web/field/documents')}
          >
            ← Belge Paneline Dön
          </button>
          <div className="epp-hero-title-wrap">
            <div className="epp-hero-icon-box">
              <span>🗺️</span>
            </div>
            <div>
              <h1 className="epp-hero-title">Tahliye & Vaziyet Planı Çizim Aracı</h1>
              <p className="epp-hero-desc">
                6331 Sayılı İSG Kanunu ve İSG Acil Durumlar Yönetmeliği kapsamında resmi ölçekli tahliye krokisi oluşturun, düzenleyin ve yüksek çözünürlükte indirin.
              </p>
            </div>
          </div>
        </div>
        <div className="epp-hero-badges-wrap">
          <span className="epp-hero-badge">TS ISO 7010 Uyumlu</span>
          <span className="epp-hero-badge epp-badge-accent">A4 Yatay Baskı</span>
        </div>
      </div>

      {/* ── STEP 1: İŞYERİ SEÇİMİ ── */}
      <div className="epp-card epp-select-card">
        <div className="epp-section-header">
          <div className="epp-section-step-indicator">
            <span className="epp-step-circle">1</span>
            <div className="epp-step-text">
              <h2 className="epp-step-title">İşyeri Seçimi</h2>
              <p className="epp-step-desc">Krokisi hazırlanacak işyerini arayın veya listeden belirleyin</p>
            </div>
          </div>
          {selectedWorkplace && (
            <button
              type="button"
              className="epp-btn-change-wp"
              onClick={() => {
                setSelectedWorkplace(null);
                setEvacuationPlanData(null);
                setSearchTerm('');
              }}
            >
              🔄 Farklı İşyeri Seç
            </button>
          )}
        </div>

        <div className="epp-search-box-wrap" ref={searchWrapRef}>
          <div className="epp-search-input-box">
            <span className="epp-search-icon">🔍</span>
            <input
              type="text"
              placeholder={selectedWorkplace ? `Seçili: ${selectedWorkplace.name}` : "Firma adı veya şehir yazarak işyeri arayın..."}
              value={searchTerm}
              onChange={(e) => {
                setSearchTerm(e.target.value);
                setDropdownOpen(true);
              }}
              onFocus={() => setDropdownOpen(true)}
              className="epp-search-input"
            />
            {selectedWorkplace && (
              <span className="epp-selected-pill">
                ✓ Aktif Seçim
              </span>
            )}
          </div>

          {dropdownOpen && (
            <div className="epp-dropdown">
              {loading ? (
                <div className="epp-dropdown-item epp-muted">⏳ İşyerleri yükleniyor...</div>
              ) : filteredWorkplaces.length === 0 ? (
                <div className="epp-dropdown-item epp-muted">❌ Aramanıza uygun kayıtlı işyeri bulunamadı.</div>
              ) : (
                filteredWorkplaces.map(w => {
                  const riskLevel = w.riskLevel || 'dangerous';
                  const riskLabel = RISK_LEVEL_LABEL[riskLevel] || 'Tehlikeli';
                  const riskBg = riskLevel === 'low' ? '#059669' : riskLevel === 'dangerous' ? '#d97706' : '#dc2626';

                  return (
                    <div
                      key={w.id}
                      className={`epp-dropdown-item ${selectedWorkplace?.id === w.id ? 'active' : ''}`}
                      onClick={() => handleSelectWorkplace(w)}
                    >
                      <div className="epp-dropdown-item-top">
                        <span className="epp-dropdown-item-title">{w.name}</span>
                        <span
                          className="epp-dropdown-risk-badge"
                          style={{ backgroundColor: riskBg }}
                        >
                          {riskLabel}
                        </span>
                      </div>
                      <div className="epp-dropdown-item-sub">
                        <span>📍 {w.city ? `${w.city} / ${w.district || ''}` : 'Adres Kayıtlı Değil'}</span>
                        {w.employeeCount ? <span>👥 {w.employeeCount} Çalışan</span> : null}
                        {(w.sskRegistrationNo || w.sgkNo) ? <span>📄 SGK: {w.sskRegistrationNo || w.sgkNo}</span> : null}
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          )}
        </div>

        {/* Seçilen İşyeri Bilgi Bandı (Acil Eylem Planı ile birebir uyumlu şık bar) */}
        {selectedWorkplace && (
          <div className="epp-selected-wp-bar">
            <div className="epp-bar-left">
              <span className="epp-bar-label">SEÇİLEN İŞYERİ :</span>
              <span className="epp-bar-name">{selectedWorkplace.name}</span>
              <span
                className="epp-bar-hazard"
                style={{
                  backgroundColor:
                    selectedWorkplace.riskLevel === 'low'
                      ? '#059669'
                      : selectedWorkplace.riskLevel === 'dangerous'
                      ? '#d97706'
                      : '#dc2626'
                }}
              >
                {RISK_LEVEL_LABEL[selectedWorkplace.riskLevel || 'dangerous']}
              </span>
            </div>

            <div className="epp-bar-details">
              <span className="epp-bar-chip">
                👥 <strong>{selectedWorkplace.employeeCount || 1} Çalışan</strong>
              </span>

              {(selectedWorkplace.sskRegistrationNo || selectedWorkplace.sgkNo) && (
                <span className="epp-bar-chip font-mono">
                  SGK: <strong>{selectedWorkplace.sskRegistrationNo || selectedWorkplace.sgkNo}</strong>
                </span>
              )}

              {selectedWorkplace.address && (
                <span className="epp-bar-chip epp-bar-chip-address" title={selectedWorkplace.address}>
                  📍 {selectedWorkplace.address}
                </span>
              )}
            </div>
          </div>
        )}
      </div>

      {/* ── STEP 2: KROKİ BİLGİLERİ & ÇİZİM ALANI ── */}
      {selectedWorkplace && (
        <div className="epp-card epp-canvas-card">
          <div className="epp-section-header">
            <div className="epp-section-step-indicator">
              <span className="epp-step-circle">2</span>
              <div className="epp-step-text">
                <h2 className="epp-step-title">Kroki Parametreleri ve Çizim Tuvali</h2>
                <p className="epp-step-desc">Toplanma yerini belirleyin, interaktif çizim motorunu açarak kat planını oluşturun</p>
              </div>
            </div>

            <div className="epp-plan-status-pill">
              {evacuationPlanData ? (
                <span className="epp-pill-success">✓ Çizim Tamamlandı & Kayıtlı</span>
              ) : (
                <span className="epp-pill-pending">⏳ Çizim Bekleniyor</span>
              )}
            </div>
          </div>

          {/* Form Alanı: Toplanma Yeri */}
          <div className="epp-config-panel">
            <div className="epp-field-group">
              <label className="epp-label">
                <span className="epp-label-icon">🏢</span>
                <span className="epp-label-text">Acil Durum Toplanma Yeri / Açık Alan Konumu</span>
              </label>
              <div className="epp-input-wrap">
                <input
                  type="text"
                  className="epp-input"
                  value={assemblyPoint}
                  onChange={(e) => setAssemblyPoint(e.target.value)}
                  placeholder="Örn: Ana Giriş Nizamiyesi Bahçesi / Açık Otopark Alanı"
                />
              </div>
              <span className="epp-hint">
                ℹ️ Bu metin hem krokinin üst başlığında hem de resmi TS ISO 7010 lejant alanında otomatik görünür.
              </span>
            </div>
          </div>

          {/* Kroki Durumu / Aksiyon Kartı */}
          <div className="epp-workspace-box">
            {!evacuationPlanData ? (
              <div className="epp-empty-state">
                <div className="epp-empty-visual">
                  <div className="epp-empty-icon-glow">🎨</div>
                </div>
                <div className="epp-empty-content">
                  <h3 className="epp-empty-heading">Kroki Çizim Aracını Başlatın</h3>
                  <p className="epp-empty-paragraph">
                    Dış ve iç duvarlar, acil kaçış kapıları, yangın söndürme tüpleri (YT), yangın dolapları (YD), ilk yardım dolapları (İY), elektrik panoları (EP), gaz vanaları (GV) ve toplanma alanlarını interaktif olarak çizin.
                  </p>
                  <div className="epp-feature-badges">
                    <span>⚡ Hızlı Duvar Çizimi (Grid Kilidi)</span>
                    <span>🖼️ Altlık Mimari Plan Yükleme</span>
                    <span>📑 TS ISO 7010 Standart Semboller</span>
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => setModalOpen(true)}
                  className="epp-btn-launch"
                >
                  <span>✏️</span> Çizim Aracını Aç
                </button>
              </div>
            ) : (
              <div className="epp-ready-state">
                <div className="epp-preview-container">
                  <div className="epp-preview-frame">
                    <img
                      src={evacuationPlanData.imageDataUrl}
                      alt="Tahliye Krokisi Önizleme"
                      className="epp-preview-image"
                    />
                  </div>
                  <span className="epp-preview-caption">Baskı Önizlemesi (1200 × 850 px • A4 Yatay)</span>
                </div>

                <div className="epp-ready-details-box">
                  <div className="epp-ready-badge-row">
                    <span className="epp-ready-verified-badge">✓ RESMİ KROKİ HAZIR</span>
                    <span className="epp-ready-date-badge">📅 {new Date().toLocaleDateString('tr-TR')}</span>
                  </div>

                  <h3 className="epp-ready-firm-name">{selectedWorkplace.name}</h3>

                  <div className="epp-stats-grid">
                    <div className="epp-stat-card">
                      <span className="epp-stat-icon">🧱</span>
                      <div>
                        <span className="epp-stat-val">{evacuationPlanData.walls.length}</span>
                        <span className="epp-stat-lbl">Duvar & Kapı</span>
                      </div>
                    </div>

                    <div className="epp-stat-card">
                      <span className="epp-stat-icon">🏷️</span>
                      <div>
                        <span className="epp-stat-val">{evacuationPlanData.labels.length}</span>
                        <span className="epp-stat-lbl">Mahal / Oda</span>
                      </div>
                    </div>

                    <div className="epp-stat-card">
                      <span className="epp-stat-icon">🧯</span>
                      <div>
                        <span className="epp-stat-val">{evacuationPlanData.symbols.length}</span>
                        <span className="epp-stat-lbl">İSG Ekipmanı</span>
                      </div>
                    </div>
                  </div>

                  <div className="epp-action-buttons-group">
                    <button
                      type="button"
                      onClick={() => setModalOpen(true)}
                      className="epp-btn-action epp-btn-action-edit"
                    >
                      <span>✏️</span> Çizimi Düzenle
                    </button>

                    <button
                      type="button"
                      onClick={handleDownloadPng}
                      className="epp-btn-action epp-btn-action-download"
                    >
                      <span>💾</span> Yüksek Çözünürlüklü İndir (PNG)
                    </button>

                    <button
                      type="button"
                      onClick={() => setEvacuationPlanData(null)}
                      className="epp-btn-action epp-btn-action-reset"
                      title="Çizimi sıfırla ve yeniden başla"
                    >
                      <span>🗑️</span> Sıfırla
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* 30 Yıllık Tecrübe ile Hazırlanmış İSG Rehber Kartları */}
          <div className="epp-guidelines-grid">
            <div className="epp-guide-card">
              <div className="epp-guide-icon">🚪</div>
              <div className="epp-guide-text">
                <h5>Kaçış Yolları ve Çıkışlar</h5>
                <p>Acil çıkış kapılarını ve koridorları yeşil kesikli çizgilerle net şekilde işaretleyin; önlerinin açık olduğunu denetleyin.</p>
              </div>
            </div>

            <div className="epp-guide-card">
              <div className="epp-guide-icon">🧯</div>
              <div className="epp-guide-text">
                <h5>Yangın Müdahale Ekipmanları</h5>
                <p>Yangın söndürme tüplerini (YT) ve yangın dolaplarını (YD) fiziksel konumlarına sadık kalarak krokide gösterin.</p>
              </div>
            </div>

            <div className="epp-guide-card">
              <div className="epp-guide-icon">⚡</div>
              <div className="epp-guide-text">
                <h5>Elektrik Panosu & Gaz Vanası</h5>
                <p>Acil durumda ilk kesilmesi gereken ana elektrik şalterlerini (EP) ve doğalgaz/LPG ana vanalarını (GV) belirleyin.</p>
              </div>
            </div>

            <div className="epp-guide-card">
              <div className="epp-guide-icon">🏢</div>
              <div className="epp-guide-text">
                <h5>Toplanma Alanı & Lejant</h5>
                <p>Yapıdan güvenli mesafede toplanma noktası seçin. Çıktıda TS ISO 7010 resmi sembol tablosu otomatik eklenir.</p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ── INTERACTIVE CANVAS DRAWING MODAL ── */}
      {selectedWorkplace && (
        <EvacuationPlanModal
          isOpen={modalOpen}
          onClose={() => setModalOpen(false)}
          onSave={(data) => setEvacuationPlanData(data)}
          workplaceName={selectedWorkplace.name}
          assemblyPoint={assemblyPoint}
          initialData={evacuationPlanData}
          saveButtonText="Krokisi Kaydet"
        />
      )}
    </div>
  );
};

export default EvacuationPlanPage;
